import React, { useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaEuroSign,
  FaInfoCircle,
  FaLayerGroup,
  FaUserTie,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import TarifsResidenceCalculator from "./TarifsResidenceCalculator";

const colorMap = {
  "bg-green-400": {
    bar: "from-emerald-500 to-teal-400",
    chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300",
  },
  "bg-red-400": {
    bar: "from-red-500 to-rose-400",
    chip: "bg-red-100 text-red-800 dark:bg-red-500/10 dark:text-red-300",
  },
  "bg-blue-400": {
    bar: "from-blue-500 to-sky-400",
    chip: "bg-blue-100 text-blue-800 dark:bg-blue-500/10 dark:text-blue-300",
  },
  "bg-yellow-400": {
    bar: "from-yellow-500 to-amber-400",
    chip: "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-300",
  },
  "bg-orange-400": {
    bar: "from-orange-500 to-amber-400",
    chip: "bg-orange-100 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300",
  },
  "bg-purple-400": {
    bar: "from-purple-500 to-fuchsia-400",
    chip: "bg-purple-100 text-purple-800 dark:bg-purple-500/10 dark:text-purple-300",
  },
  "bg-pink-400": {
    bar: "from-pink-500 to-rose-400",
    chip: "bg-pink-100 text-pink-800 dark:bg-pink-500/10 dark:text-pink-300",
  },
  "bg-indigo-400": {
    bar: "from-indigo-500 to-blue-400",
    chip: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/10 dark:text-indigo-300",
  },
  "bg-teal-400": {
    bar: "from-teal-500 to-cyan-400",
    chip: "bg-teal-100 text-teal-800 dark:bg-teal-500/10 dark:text-teal-300",
  },
  "bg-gray-400": {
    bar: "from-slate-500 to-slate-400",
    chip: "bg-slate-100 text-slate-800 dark:bg-white/10 dark:text-slate-300",
  },
  "bg-black": {
    bar: "from-slate-950 to-slate-700",
    chip: "bg-slate-100 text-slate-800 dark:bg-white/10 dark:text-slate-300",
  },
};

const getColorConfig = (color) => colorMap[color] || colorMap["bg-orange-400"];

const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const normalized = String(value)
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

  const amount = Number.parseFloat(normalized);

  return Number.isFinite(amount) ? amount : null;
};

const formatEuro = (value) => {
  if (!Number.isFinite(value)) return "—";

  return `${value.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

const tokenizeRanges = (text) =>
  String(text || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const getTeachers = (group) =>
  [group?.enseignant1, group?.enseignant2].filter(Boolean).join(" & ");

const getScheduleLines = (group) =>
  [group?.horaire1, group?.horaire2].filter(Boolean);

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="h-2 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    <div className="mt-5 h-8 w-44 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    <div className="mt-5 space-y-3">
      <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-5/6 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-4/6 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
    <div className="mt-6 h-11 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
  </div>
);

const EmptyState = () => (
  <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/75 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
      <FaLayerGroup />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucun groupe trouvé
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Aucun groupe ne correspond au filtre ou à la recherche sélectionnée.
    </p>
  </div>
);

const PriceBox = ({ label, value, important = false }) => (
  <div
    className={`rounded-2xl border p-4 ${
      important
        ? "border-orange-200 bg-orange-50 text-orange-900 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-200"
        : "border-slate-200 bg-slate-50 text-slate-800 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
    }`}
  >
    <p className="text-xs font-bold uppercase tracking-[0.16em] opacity-60">
      {label}
    </p>
    <p className="mt-1 font-barlow text-2xl font-bold">{value}</p>
  </div>
);

const GroupCard = ({ group, index, isTarifOpen, onToggleTarif }) => {
  const color = getColorConfig(group?.color_groupe);
  const teachers = getTeachers(group);
  const scheduleLines = getScheduleLines(group);
  const ranges = tokenizeRanges(group?.annees_naissance);

  const licence = parseAmount(group?.tarif_licence);
  const cotisation = parseAmount(group?.tarif_cotisation);

  const total = useMemo(() => {
    if (!Number.isFinite(licence) && !Number.isFinite(cotisation)) return null;

    return Number(licence || 0) + Number(cotisation || 0);
  }, [licence, cotisation]);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div className={`h-2 bg-gradient-to-r ${color.bar}`} />

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Groupe
            </p>
            <h3 className="mt-2 font-barlow text-3xl font-bold leading-tight text-slate-950 dark:text-white">
              {group?.nom || `Groupe ${index + 1}`}
            </h3>
          </div>

          {/* <div className={`rounded-full px-3 py-1.5 text-xs font-bold ${color.chip}`}>
            {ranges.length ? ranges.length : 0} tranche
            {ranges.length > 1 ? "s" : ""}
          </div> */}
        </div>

        {ranges.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {ranges.map((range) => (
              <span
                key={range}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              >
                {range}
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 space-y-4">
          <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white">
              <FaCalendarAlt className="text-primary" />
              Horaires
            </div>

            {scheduleLines.length > 0 ? (
              <div className="space-y-2">
                {scheduleLines.map((line, lineIndex) => (
                  <p
                    key={`${line}-${lineIndex}`}
                    className="rounded-2xl bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-sm dark:bg-slate-950 dark:text-slate-300"
                  >
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Horaires non renseignés.</p>
            )}
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white">
              <FaUserTie className="text-primary" />
              Enseignant(s)
            </div>

            <p className="text-sm font-semibold leading-6 text-slate-600 dark:text-slate-300">
              {teachers || "Non renseigné"}
            </p>
          </div>
        </div>

        <div className="mt-auto pt-6">
          <button
            type="button"
            onClick={onToggleTarif}
            aria-expanded={isTarifOpen}
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
          >
            <FaEuroSign />
            {isTarifOpen ? "Masquer les tarifs" : "Afficher les tarifs"}
            {isTarifOpen ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          <div
            className={`overflow-hidden transition-all duration-500 ${
              isTarifOpen ? "mt-5 max-h-96 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <PriceBox label="Licence" value={formatEuro(licence)} />
              <PriceBox label="Cotisation" value={formatEuro(cotisation)} />
              <PriceBox
                label="Total annuel"
                value={formatEuro(total)}
                important
              />
            </div>

            <p className="mt-4 flex items-start gap-2 rounded-2xl bg-slate-50 p-3 text-xs leading-5 text-slate-500 dark:bg-white/5 dark:text-slate-400">
              <FaInfoCircle className="mt-0.5 shrink-0 text-primary" />
              Les tarifs affichés sont donnés à titre indicatif selon les
              informations renseignées par le club.
            </p>
          </div>
        </div>
      </div>
    </article>
  );
};

export default function GroupHorairesTarifs({ groups = [], loading = false }) {
  const [openTarifs, setOpenTarifs] = useState(() => new Set());

  const allOpen =
    groups.length > 0 && groups.every((group) => openTarifs.has(group.id));

  const toggleTarif = (id) => {
    setOpenTarifs((previous) => {
      const next = new Set(previous);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const toggleAllTarifs = () => {
    setOpenTarifs(() => {
      if (allOpen) return new Set();

      return new Set(groups.map((group) => group.id));
    });
  };

  return (
    <section className="relative overflow-hidden bg-white py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:42px_42px] opacity-30 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
              <FaLayerGroup />
              Groupes du club
            </div>

            <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
              Groupes, horaires <span className="text-primary">& tarifs</span>
            </h2>

            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
              Consultez les groupes disponibles, les années de naissance
              concernées, les créneaux et le détail des tarifs.
            </p>
          </div>

          {!loading && groups.length > 0 && (
            <button
              type="button"
              onClick={toggleAllTarifs}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
            >
              {allOpen ? "Masquer tous les tarifs" : "Afficher tous les tarifs"}
              {allOpen ? <FaChevronUp /> : <FaChevronDown />}
            </button>
          )}
        </Reveal>

        <Reveal className="mt-10" variant="zoom" delay={120}>
          <TarifsResidenceCalculator />
        </Reveal>

        {/* <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {loading &&
            Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard key={`group-skeleton-${index}`} />
            ))}

          {!loading && groups.length === 0 && <EmptyState />}

          {!loading &&
            groups.map((group, index) => (
              <Reveal
                key={group.id || `${group?.nom}-${index}`}
                variant="zoom"
                delay={index * 70}
              >
                <GroupCard
                  group={group}
                  index={index}
                  isTarifOpen={openTarifs.has(group.id)}
                  onToggleTarif={() => toggleTarif(group.id)}
                />
              </Reveal>
            ))}
        </div> */}
      </div>
    </section>
  );
}
